import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../api/client';

// Shared across components: identical requests are de-duplicated and reused for 5s.
const inflight = new Map();
const cache = new Map();

const fetchCoin = (symbol, timeframe) => {
    const key = `${symbol}|${timeframe}`;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < 5000) return Promise.resolve(hit.data);
    if (inflight.has(key)) return inflight.get(key);
    const p = apiClient
        .get(`/market/coin/${symbol}`, { params: { timeframe } })
        .then(({ data }) => {
            cache.set(key, { data, at: Date.now() });
            return data;
        })
        .finally(() => inflight.delete(key));
    inflight.set(key, p);
    return p;
};

/** Live coin analysis for the selected symbol. Never returns another coin's data. */
export function useCoinData(symbol, timeframe = 'short', intervalMs = 15000) {
    const [state, setState] = useState({ data: null, error: null, loading: false, updatedAt: null });
    const current = useRef(symbol);

    const load = useCallback(async () => {
        if (!symbol) return;
        setState((s) => ({ ...s, loading: true }));
        try {
            const data = await fetchCoin(symbol, timeframe);
            if (current.current !== symbol || data?.symbol !== symbol) return;
            setState({ data, error: null, loading: false, updatedAt: Date.now() });
        } catch (e) {
            if (current.current !== symbol) return;
            setState((s) => ({ ...s, loading: false, error: e?.response?.data?.detail || 'Veri alınamadı' }));
        }
    }, [symbol, timeframe]);

    useEffect(() => {
        current.current = symbol;
        setState({ data: null, error: null, loading: !!symbol, updatedAt: null });
        load();
        const id = setInterval(load, intervalMs);
        return () => clearInterval(id);
    }, [symbol, timeframe, load, intervalMs]);

    return { ...state, reload: load };
}

export function useNow(ms = 1000) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), ms);
        return () => clearInterval(id);
    }, [ms]);
    return now;
}
